import * as React from "react"
import { Plus, Menu, Search, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar"
import { OfflineIndicator } from "@/components/offline/offline-indicator"
import { GroupSelector } from "@/components/groups/group-selector"
import { GlobalSearch } from "@/components/layout/global-search"
import { cn } from "@/lib/utils"
import type { User, Group } from "@/types/domain.types"

interface HeaderProps {
  user?: User
  currentGroup?: Group
  title?: string
  subtitle?: string
  onMenuToggle?: () => void
  onAddExpense?: () => void
  showSearch?: boolean
  showAddButton?: boolean
  className?: string
}

export const Header: React.FC<HeaderProps> = ({
  user,
  currentGroup: _currentGroup,
  title,
  subtitle,
  onMenuToggle,
  onAddExpense,
  showSearch = false,
  showAddButton = false,
  className,
}) => {
  const [mobileSearchOpen, setMobileSearchOpen] = React.useState(false)

  return (
    <header
      className={cn(
        "flex flex-col bg-background border-b",
        className
      )}
    >
      <div className="flex items-center justify-between px-4 py-3">
      {/* Left Section */}
      <div className="flex items-center gap-4">
        {/* Mobile Menu Button */}
        {onMenuToggle && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onMenuToggle}
            className="lg:hidden"
          >
            <Menu className="h-5 w-5" />
          </Button>
        )}

        {/* Title */}
        <div className="min-w-0">
          {title && (
            <h1 className="text-xl font-semibold truncate">{title}</h1>
          )}
          {subtitle && (
            <p className="text-sm text-muted-foreground truncate">{subtitle}</p>
          )}
        </div>

      </div>

      {/* Center Section - Search */}
      {showSearch && (
        <div className="flex-1 max-w-md mx-4 hidden md:block">
          <GlobalSearch />
        </div>
      )}

      {/* Right Section */}
      <div className="flex items-center gap-2">
        {/* Mobile Search Toggle */}
        {showSearch && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileSearchOpen((open) => !open)}
            className="md:hidden"
            aria-label={mobileSearchOpen ? "Close search" : "Search"}
            aria-expanded={mobileSearchOpen}
          >
            {mobileSearchOpen ? <X className="h-5 w-5" /> : <Search className="h-5 w-5" />}
          </Button>
        )}

        {/* Group Selector - Desktop Only */}
        <div className="hidden lg:block">
          <GroupSelector />
        </div>

        {/* Offline Indicator */}
        <OfflineIndicator />

        {/* Add Button */}
        {showAddButton && onAddExpense && (
          <Button onClick={onAddExpense} size="sm" className="hidden sm:flex">
            <Plus className="mr-2 h-4 w-4" />
            Add Expense
          </Button>
        )}

        {/* Mobile Add Button */}
        {showAddButton && onAddExpense && (
          <Button onClick={onAddExpense} size="icon" className="sm:hidden">
            <Plus className="h-4 w-4" />
          </Button>
        )}

        {/* User Avatar (Mobile) */}
        {user && (
          <Avatar className="h-8 w-8 lg:hidden">
            <AvatarImage src={user.avatar_url} />
            <AvatarFallback>{user.name.charAt(0)}</AvatarFallback>
          </Avatar>
        )}
      </div>
      </div>

      {/* Mobile Search Bar - expands below the header row */}
      {showSearch && mobileSearchOpen && (
        <div className="px-4 pb-3 md:hidden">
          <GlobalSearch autoFocus onSelect={() => setMobileSearchOpen(false)} />
        </div>
      )}
    </header>
  )
}
